import { createSkyLevelAtlas, paintSkyFlightMenu } from './213-sky-level-atlas.mjs';
import { skyFlightCapabilities } from '../../../packages/domain/src/106-sky-capabilities.mjs';
import { createSkyControlSettings } from './175-sky-control-settings.mjs';
import { createSkyFlightIndicators, skyInputDescription } from './178-sky-flight-indicators.mjs';
import {
  createSkyRun,
  continueSkyRun,
  buySkyUpgrade,
  SKY_STEP,
  SKY_WORLDS,
} from '../../../packages/domain/src/75-sky-rider.mjs';
import firstGuardianRig from '../assets/sky-rider/31-original-dragon-rig.json' with { type: 'json' };
import { createSkyRenderer } from './111-sky-renderer.mjs';
import { createSkyPlayerLoop } from './243-sky-loop-timing.mjs';
import { createSkyAudio } from './116-sky-audio.mjs';
import { skyCinematicFrame, skyCinematicCopy, skyEndingKind } from './117-sky-cinematics.mjs';
import { skyEquipment } from '../../../packages/domain/src/77-sky-upgrades.mjs';
import { skyStageArtVariant } from './134-sky-asset-manifest.mjs';
import { createSkyPreload } from './131-sky-preload.mjs';
import { createSkyFlightHud } from './132-sky-flight-hud.mjs';
import { createSkyTraversalHud } from './139-sky-traversal-hud.mjs';
import { createSkySteeringPad } from './166-sky-steering-pad.mjs';
import { acceptSkyEncounterData } from './167-sky-leviathan-collision.mjs';
import { createSkyFlightInput } from './164-sky-flight-input.mjs';
export { skyBonusTelemetry } from './132-sky-flight-hud.mjs';
import { createSkyUpgradeDock } from './121-sky-upgrade-dock.mjs';

/** Private flight controller: one RAF, explicit resume, cancellable lazy graphics,
 * relative touch steering and scoped keyboard input. No account or score writes. */
export function createSkyPlayer(
  ctx,
  { host, seed, allowed, visible, onStart, onProgress, rendererFactory = createSkyRenderer },
) {
  const e = ctx.e;
  const surface = e('div', 'sky-player');
  let canvas = e('canvas', 'sky-canvas');
  canvas.tabIndex = 0;
  canvas.setAttribute(
    'aria-label',
    'Sky Rider. WASD moves; mouse or arrow keys aim. Left pad moves; right AIM pad aims. Space releases Nova. P pauses.',
  );
  const hud = e('div', 'sky-hud');
  const score = e('span', 'sky-score');
  const shields = e('span', 'sky-shields');
  const pauseButton = e('button', 'sky-button', 'Pause');
  const pulseButton = e('button', 'sky-button sky-nova', 'Nova');
  const banner = e('div', 'sky-banner');
  const telemetry = e('div', 'sky-telemetry');
  telemetry.setAttribute('role', 'group');
  const progress = e('progress', 'sky-progress');
  progress.max = SKY_WORLDS.length;
  progress.setAttribute('aria-label', 'Expedition progress');
  const feedback = e('div', 'sky-feedback');
  const precision = e('div', 'sky-precision');
  const precisionLabel = e('span', 'sky-precision-label');
  const precisionMeter = e('progress', 'sky-precision-meter');
  precisionMeter.max = 6;
  precisionMeter.setAttribute('aria-label', 'Precision hits toward overdrive');
  const precisionImpact = e('span', 'sky-precision-impact');
  precision.append(precisionLabel, precisionMeter, precisionImpact);
  const flash = e('div', 'sky-impact');
  const bossBar = e('progress', 'sky-boss');
  bossBar.setAttribute('aria-label', 'Guardian shields');
  const cinema = e('div', 'sky-cinema');
  const cinemaLabel = e('p', 'sky-cinema-label');
  const cinemaTitle = e('h3', 'sky-cinema-title');
  const cinemaCopy = e('p', 'sky-cinema-copy');
  const skip = e('button', 'sky-button sky-skip', 'Skip sequence');
  const hold = e('button', 'sky-button sky-hold', 'Pause sequence');
  cinemaCopy.setAttribute('aria-live', 'polite');
  cinema.append(cinemaLabel, cinemaTitle, cinemaCopy, skip, hold);
  const overlay = e('div', 'sky-overlay');
  const eyebrow = e('p', 'sky-eyebrow', 'AN ORIGINAL ARCADE EXPEDITION');
  const title = e('h3', 'sky-title', 'SKY RIDER');
  const copy = e('p', 'sky-copy');
  const start = e('button', 'sky-launch', 'Launch expedition');
  const restart = e('button', 'sky-button', 'New expedition');
  const sound = e('button', 'sky-button', 'Sound off');
  const hint = e('p', 'sky-hint', 'LEFT PAD MOVES · RIGHT PAD AIMS · AUTO FIRE');
  const status = e('p', 'sky-status');
  status.setAttribute('role', 'status');
  const debrief = e('p', 'sky-debrief');
  const upgradeDock = createSkyUpgradeDock(e);
  const upgrades = upgradeDock.element;
  const upgradeButtons = upgradeDock.buttons;
  const atlasHost = e('div', 'sky-atlas-host');
  const menuLabel = e('p', 'sky-menu-label', 'FLIGHT CONTROL');
  const actions = e('nav', 'sky-menu-actions');
  actions.setAttribute('aria-label', 'Flight controls');
  actions.append(start, restart, sound);
  overlay.append(eyebrow, title, debrief, copy, atlasHost, upgrades, menuLabel, actions, status);
  hud.append(score, shields, pauseButton);
  surface.append(
    canvas,
    flash,
    hud,
    telemetry,
    progress,
    banner,
    bossBar,
    feedback,
    precision,
    pulseButton,
    hint,
    overlay,
    cinema,
  );
  host.append(surface);
  const selection = { startSector: 0, startMode: 'full' };
  let state = createSkyRun(seed, { firstGuardianRig }),
    renderer = null,
    disposed = false,
    pending = false,
    generation = 0,
    launchEpoch = 0;
  let raf = 0,
    last = null,
    accumulator = 0,
    pulse = false,
    skipTraversal = false;
  let outro = null;
  let sequence = null,
    sequencePaused = false;
  let graphicsError = false,
    soundOn = false,
    purchaseMessage = '';
  let soundPending = false,
    soundEpoch = 0;
  const audio = createSkyAudio();
  const loop = createSkyPlayerLoop(() => canvas);
  const controller = new AbortController();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const traversalHud = createSkyTraversalHud({
    e,
    surface,
    signal: controller.signal,
    onSkip: () => {
      if (canPlay() && state.status === 'playing') {
        skipTraversal = true;
        canvas.focus();
      }
    },
  });
  const flightHud = createSkyFlightHud({
    telemetry,
    progress,
    precision,
    precisionLabel,
    precisionMeter,
    precisionImpact,
    feedback,
    flash,
    reduced: () => motion.matches,
  });
  const listen = (node, name, fn, options = {}) =>
    node.addEventListener(name, fn, { ...options, signal: controller.signal });
  const canPlay = () => !disposed && allowed() && visible() && !document.hidden;
  const preload = createSkyPreload({
    renderer: () => renderer,
    accept: (requested, owner) => acceptSkyEncounterData(state, requested, owner),
    changed: () => {
      if (disposed) return;
      if (
        sequence?.kind === 'arrival' &&
        !sequencePaused &&
        canPlay() &&
        cinematicFrame(sequence.kind, sequence.age, motion.matches).done &&
        preload.read(state) === 'ready'
      )
        finishSequence();
      paint();
    },
  });
  const atlas = createSkyLevelAtlas({
    e,
    listen,
    select: (sector, startMode) => {
      if (
        !canPlay() ||
        pending ||
        sequence ||
        !['ready', 'over', 'complete'].includes(state.status)
      )
        return;
      selection.startSector = sector ?? selection.startSector;
      selection.startMode = startMode ?? selection.startMode;
      resetRun();
      paint();
    },
  });
  atlasHost.append(atlas.element);
  function resetRun() {
    outro = null;
    state = createSkyRun(seed, { firstGuardianRig, ...selection });
    flightInput.reset();
    renderer?.resetSight?.();
    flightHud.reset();
    purchaseMessage = '';
  }
  function prepareFlight(retry = false, briefing = false) {
    if (state.sector >= SKY_WORLDS.length) return;
    preload.request(state, retry, briefing);
    if (state.sector + 1 < SKY_WORLDS.length)
      preload.request({ sector: state.sector + 1 }, false, false, true);
  }
  function cinematicFrame(kind, age, quiet) {
    return (
      renderer?.cinematicFrame?.(kind, age, quiet, state.sector, skyStageArtVariant(state)) ??
      skyCinematicFrame(kind, age, quiet)
    );
  }
  const indicators = createSkyFlightIndicators({ e, surface });
  const controlSettings = createSkyControlSettings({
    e,
    listen,
    onChange: (settings) => {
      surface.classList.toggle('sky-controls-mirrored', settings.mirrored);
      flightInput.clear();
      paint();
    },
  });
  const controlDisclosure = controlSettings.disclosure;
  overlay.append(controlDisclosure);
  const steeringPad = createSkySteeringPad({
    getSettings: () => controlSettings.getSettings(),
    e,
    surface,
    getState: () => state,
    canPlay,
    hasSequence: () => Boolean(sequence),
    listen,
    focusCanvas: () => canvas.focus({ preventScroll: true }),
    onPause: () => {
      pause();
      start.focus();
    },
  });
  const flightInput = createSkyFlightInput({
    e,
    surface,
    getSettings: () => controlSettings.getSettings(),
    steering: () => steeringPad.sample(),
    clearSteering: () => steeringPad.clear(),
    getCanvas: () => canvas,
    getState: () => state,
    canPlay,
    hasSequence: () => Boolean(sequence),
    listen,
    onPause: () => {
      pause();
      start.focus();
    },
    onPulse: () => {
      pulse = true;
    },
    aimPoint: (ndc, options) => renderer?.aimPoint?.(ndc, options) ?? null,
  });
  function clearInput() {
    flightInput.clear();
    pulse = false;
    skipTraversal = false;
  }
  let labelledCanvas = null,
    canvasLabel = null;
  function paint() {
    if (disposed) return;
    const cinematic = Boolean(sequence && !sequencePaused);
    const playing = state.status === 'playing' && !cinematic;
    const combat = playing && !state.traversal;
    flightInput.syncMode();
    steeringPad.paint();
    surface.dataset.bossControl = state.leviathan ? 'leviathan' : (state.boss?.controlMode ?? '');
    const freeFlight = state.freeFlight === true;
    surface.dataset.freeFlight = String(freeFlight);
    const description = skyInputDescription(state, controlSettings.getSettings().mirrored);
    if (labelledCanvas !== canvas || canvasLabel !== description.label) {
      canvas.setAttribute('aria-label', description.label);
      labelledCanvas = canvas;
      canvasLabel = description.label;
    }
    hint.textContent = description.hint;
    indicators.paint(state, playing);
    traversalHud.paint(state, playing, controlSettings.getSettings());
    const intermission = state.status === 'intermission';
    surface.dataset.phase = cinematic ? 'cinematic' : state.status;
    cinema.hidden = !cinematic;
    if (cinematic) {
      const shot = cinematicFrame(sequence.kind, sequence.age, motion.matches);
      cinema.dataset.shot = shot.shot;
      cinemaLabel.hidden = cinemaTitle.hidden = cinemaCopy.hidden = Boolean(shot.briefing);
      const caption = skyCinematicCopy(sequence.kind, shot, state);
      cinemaLabel.textContent = caption.label;
      cinemaTitle.textContent = caption.title;
      cinemaCopy.textContent = caption.copy;
      const waiting =
        sequence.kind === 'arrival' &&
        (shot.done || shot.briefingWaiting) &&
        preload.read(state) !== 'ready';
      const failed = waiting && preload.read(state) === 'error';
      skip.disabled = waiting && !failed;
      skip.textContent = failed ? 'Retry loading' : waiting ? 'Preparing stage…' : 'Skip sequence';
      if (waiting) {
        cinemaLabel.hidden = cinemaTitle.hidden = cinemaCopy.hidden = false;
        cinemaLabel.textContent = 'FLIGHT ON HOLD';
        cinemaTitle.textContent = failed ? 'CONNECTION INTERRUPTED' : 'PREPARING THE NEXT FLIGHT';
        cinemaCopy.textContent = failed
          ? 'Stage artwork could not load. Retry, or pause to return to flight controls.'
          : 'Finishing stage artwork. Your flight will start when it is ready.';
      }
    }
    const world = SKY_WORLDS[Math.min(SKY_WORLDS.length - 1, state.sector)];
    host.dataset.playing = String(playing || intermission || cinematic);
    surface.dataset.playing = String(playing);
    hud.hidden = !playing;
    telemetry.hidden = progress.hidden = !combat;
    flightHud.paint(state, combat, world);
    upgrades.hidden = !intermission || Boolean(sequence);
    debrief.hidden = !intermission;
    if (intermission) {
      debrief.textContent = `+${state.stageResult?.score.toLocaleString() ?? 0} POINTS · ${state.stageResult?.squads ?? 0} WAVES CLEARED · ${state.salvage} SALVAGE`;
      upgradeDock.render(state);
    }
    overlay.hidden = playing || cinematic;
    menuLabel.textContent = intermission ? 'LOADOUT READY / CONTINUE' : 'FLIGHT CONTROL';
    pulseButton.hidden = !combat;
    pulseButton.disabled =
      !state.pulse || world.kind === 'bonus' || !skyFlightCapabilities(state).weapons;
    pulseButton.textContent =
      world.kind === 'bonus' ? 'Safe passage' : state.pulse ? 'Nova · Space' : 'Nova spent';
    hint.hidden = !combat || !!state.boss || state.sectorTime > 6;
    score.textContent = String(state.score).padStart(6, '0');
    shields.textContent = `${'◆'.repeat(state.lives)}${'◇'.repeat(Math.max(0, skyEquipment(state.upgrades).maxLives - state.lives))}`;
    shields.setAttribute('aria-label', `${state.lives} shields remaining`);
    banner.textContent =
      state.sectorTime < 4
        ? `${String(state.sector + 1).padStart(2, '0')} / ${world.name}`
        : state.leviathan
          ? state.boss.name
          : state.boss
            ? `${state.boss.name} · ${state.boss.defeated ? '✓' : state.boss.vulnerable ? '◎' : '◇'}`
            : world.kind === 'bonus'
              ? 'SAFE PASSAGE · COLLECT RINGS'
              : state.journey
                ? state.journey.phase === 'handoff'
                  ? 'APPROACH CLEAR · GUARDIAN INBOUND'
                  : `FORMATION ${Math.min(state.journey.waveCount, state.journey.cleared + 1)}/${state.journey.waveCount} · ${state.journey.defeated}/${state.journey.requiredKills} TARGETS CLEARED`
                : state.sectorTime >= world.approachSeconds - 3
                  ? 'GUARDIAN APPROACHING · WATCH THE CREST'
                  : `${(state.chapterIndex ?? 0) + 1}/6 · ${state.chapterLabel ?? 'SCOUT SCREEN'}`;
    banner.hidden = !combat;
    bossBar.hidden = !combat || !state.boss;
    if (state.boss) {
      bossBar.max = state.boss.maxHp;
      bossBar.value = state.boss.hp;
    }
    paintSkyFlightMenu({
      state,
      world,
      description,
      sequence,
      sequencePaused,
      pending,
      graphicsError,
      purchaseMessage,
      title,
      eyebrow,
      copy,
      start,
      restart,
      status,
    });
    atlas.paint(
      selection.startSector,
      !sequence && ['ready', 'over', 'complete'].includes(state.status),
      pending,
      selection.startMode,
    );
    const view = sequence ?? outro;
    try {
      renderer?.draw(
        state,
        view
          ? {
              ...cinematicFrame(view.kind, view.age, motion.matches),
              age: view.age,
              kind: view.kind,
              scene: view.scene,
            }
          : null,
      );
    } catch {
      unavailable();
    }
  }
  function pause() {
    if (disposed) return;
    launchEpoch++;
    if (sequence) sequencePaused = true;
    if (state.status === 'playing') state.status = 'paused';
    cancelAnimationFrame(raf);
    raf = 0;
    last = null;
    accumulator = 0;
    clearInput();
    audio.stop();
    if (soundPending) {
      soundEpoch++;
      soundPending = false;
      soundOn = false;
      void audio.enable(false);
      sound.disabled = false;
      sound.textContent = 'Sound off';
      sound.setAttribute('aria-pressed', 'false');
    }
    paint();
  }
  function unavailable() {
    renderer?.dispose();
    renderer = null;
    preload.reset();
    graphicsError = true;
    pause();
  }
  async function initialize() {
    if (renderer || pending || !canPlay()) return Boolean(renderer);
    pending = true;
    if (graphicsError) {
      const fresh = canvas.cloneNode(false);
      canvas.replaceWith(fresh);
      canvas = fresh;
      attachCanvasInput();
    }
    graphicsError = false;
    const epoch = ++generation;
    // A restored WebGL context must not inherit an abandoned renderer's state.
    const replacement = canvas;
    paint();
    try {
      const next = await rendererFactory(replacement, {
        valid: () => canPlay() && epoch === generation,
        failed: unavailable,
        reduced: () => motion.matches,
        prototypeDragonURL: '/assets/sky-rider/30-original-dragon.glb',
        prototypeDragonRig: state.firstGuardianRig,
        initialState: { ...state },
      });
      if (!canPlay() || epoch !== generation) {
        next?.dispose();
        return false;
      }
      renderer = next;
      preload.reset();
    } catch {
      graphicsError = true;
    } finally {
      pending = false;
      if (!disposed) paint();
    }
    return Boolean(renderer);
  }
  function frame(now) {
    raf = 0;
    if (!canPlay()) {
      pause();
      return;
    }
    if (sequence) {
      if (sequencePaused) return;
      if (
        last !== null &&
        !cinematicFrame(sequence.kind, sequence.age, motion.matches).briefingWaiting
      )
        sequence.age += Math.min(0.1, Math.max(0, (now - last) / 1000));
      last = now;
      if (cinematicFrame(sequence.kind, sequence.age, motion.matches).done) finishSequence();
      paint();
      if (
        !raf &&
        ((sequence && !cinematicFrame(sequence.kind, sequence.age, motion.matches).done) ||
          (!sequence && state.status === 'playing'))
      )
        raf = requestAnimationFrame(frame);
      return;
    }
    if (state.status !== 'playing') return;
    loop.begin(state);
    if (last !== null) accumulator += Math.min(0.1, Math.max(0, (now - last) / 1000));
    last = now;
    while (accumulator >= SKY_STEP && state.status === 'playing') {
      loop.tick(state, flightInput, renderer, pulse, skipTraversal, motion.matches);
      pulse = false;
      skipTraversal = false;
      accumulator -= SKY_STEP;
    }
    loop.finish(state, prepareFlight, audio, paint);
    if (state.status === 'playing') raf = requestAnimationFrame(frame);
    else {
      clearInput();
      const ending = skyEndingKind(state);
      if (ending) beginSequence(ending);
      else start.focus();
    }
  }
  async function launch() {
    if (!canPlay() || pending) return;
    const epoch = ++launchEpoch;
    if (!sequence && ['over', 'complete'].includes(state.status)) {
      resetRun();
    }
    if (!(await initialize()) || !canPlay() || epoch !== launchEpoch) return;
    onStart?.();
    purchaseMessage = '';
    if (sequence) {
      sequencePaused = false;
      last = null;
      paint();
      if (!raf) raf = requestAnimationFrame(frame);
    } else if (state.status === 'paused') {
      prepareFlight();
      if (preload.read(state) === 'ready') startFlight();
      else {
        beginSequence('arrival');
        sequence.age = cinematicFrame('arrival', 0, motion.matches).duration;
        paint();
      }
    } else beginSequence('arrival');
  }
  function startFlight() {
    if (state.status === 'intermission') continueSkyRun(state);
    else state.status = 'playing';
    audio.sync(state);
    const epoch = launchEpoch;
    void audio.resume().then((ready) => {
      if (ready && epoch === launchEpoch && canPlay() && state.status === 'playing')
        audio.cue('launch');
    });
    last = null;
    accumulator = 0;
    canvas.focus();
    paint();
    if (!raf) raf = requestAnimationFrame(frame);
  }
  function beginSequence(kind) {
    clearInput();
    prepareFlight(false, kind === 'arrival');
    sequence = {
      kind,
      age: 0,
      scene:
        kind === 'arrival'
          ? null
          : {
              sector:
                state.stageResult && state.status !== 'over'
                  ? state.stageResult.sector
                  : state.sector,
              boss: state.boss ? { ...state.boss } : null,
            },
    };
    sequencePaused = false;
    last = null;
    accumulator = 0;
    paint();
    skip.focus();
    if (!raf) raf = requestAnimationFrame(frame);
  }
  function finishSequence() {
    const kind = sequence?.kind;
    if (kind === 'arrival' && preload.read(state) !== 'ready') {
      sequence.waitingForAssets = true;
      sequence.age = cinematicFrame(kind, 0, motion.matches).duration;
      last = null;
      paint();
      return;
    }
    if (['pilot-down', 'guardian-escape', 'expedition-complete'].includes(kind))
      outro = { ...sequence, age: cinematicFrame(kind, 0, motion.matches).duration };
    sequence = null;
    sequencePaused = false;
    last = null;
    if (kind === 'arrival') {
      renderer?.releaseBriefing?.(state);
      startFlight();
    } else {
      cancelAnimationFrame(raf);
      raf = 0;
      paint();
      if (['pilot-down', 'guardian-escape', 'expedition-complete'].includes(kind))
        onProgress?.({ completed: state.status === 'complete', score: state.score });
      start.focus();
    }
  }
  listen(hold, 'click', () => {
    pause();
    start.focus();
  });
  listen(skip, 'keydown', (event) => {
    if (event.key === 'p' || event.key === 'Escape') {
      event.preventDefault();
      pause();
      start.focus();
    }
  });
  listen(skip, 'click', () => {
    if (!canPlay() || !sequence || sequencePaused) return;
    if (sequence.kind === 'arrival' && preload.read(state) === 'error') return prepareFlight(true);
    finishSequence();
  });
  listen(start, 'click', launch);
  listen(restart, 'click', () => {
    pause();
    outro = null;
    sequence = null;
    sequencePaused = false;
    resetRun();
    paint();
  });
  listen(pauseButton, 'click', () => {
    pause();
    start.focus();
  });
  listen(pulseButton, 'click', () => {
    if (canPlay() && state.status === 'playing' && !sequence) pulse = true;
    canvas.focus();
  });
  for (const { key, button } of upgradeButtons)
    listen(button, 'click', () => {
      if (!canPlay() || sequence || state.status !== 'intermission') return;
      const result = buySkyUpgrade(state, key);
      purchaseMessage = result.ok
        ? 'Upgrade installed. Ready when you are.'
        : 'Upgrade unavailable.';
      if (result.ok) {
        upgradeDock.select(key);
        audio.cue('upgrade');
      }
      paint();
    });
  listen(sound, 'click', async () => {
    if (soundPending || !canPlay()) return;
    const epoch = ++soundEpoch;
    soundPending = true;
    sound.disabled = true;
    const next = await audio.enable(!soundOn);
    if (epoch !== soundEpoch || disposed) return;
    soundPending = false;
    sound.disabled = false;
    soundOn = next;
    sound.textContent = soundOn ? 'Sound on' : 'Sound off';
    sound.setAttribute('aria-pressed', String(soundOn));
    if (soundOn && canPlay()) audio.cue('ring');
  });
  function attachCanvasInput() {
    flightInput.attach();
  }
  attachCanvasInput();
  listen(window, 'blur', pause);
  listen(document, 'visibilitychange', () => {
    if (document.hidden) pause();
  });
  listen(motion, 'change', () => {
    if (sequence?.waitingForAssets)
      sequence.age = cinematicFrame(sequence.kind, 0, motion.matches).duration;
    paint();
  });
  const resize = new ResizeObserver(() => {
    if (state.status !== 'playing') paint();
  });
  resize.observe(surface);
  paint();
  return {
    pause,
    cancel: pause,
    refresh() {
      if (!canPlay()) pause();
    },
    dispose() {
      if (disposed) return;
      pause();
      disposed = true;
      preload.reset();
      generation++;
      controller.abort();
      resize.disconnect();
      renderer?.dispose();
      renderer = null;
      audio.dispose();
      loop.dispose();
      surface.remove();
    },
  };
}
